
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Select,
    CircularProgress,
    Paper,
    useTheme,
    Grid,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { AxiosInstance } from '@service/axios-instance';
import DataTable, { TableColumn } from 'react-data-table-component';

import { getListChecklistConfigsVersions, assignChecklistToDevice, assignChecklistToDeviceBulk, ChecklistConfigVersion, getEquipaments, getSectors, Equipament } from '@pages/checklist-config/api';
import { getCurrentOperationConfig, saveCurrentOperationConfig } from '@pages/operations-config/api';


export function ChecklistAssignmentPage() {
    const { operation, mainUser } = useContext(GlobalContext);
    const isAdmin = mainUser?.roles?.some?.((role: string) => ['ADMINISTRADOR', 'SUPERADMINISTRADOR'].includes(role));

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [usersDBVersion, setUsersDBVersion] = useState(0);
    const [accessMode, setAccessMode] = useState<string>('selected_machines');
    const [expirationCheckMode, setExpirationCheckMode] = useState<string>('disabled');
    const [confirmConfigOpen, setConfirmConfigOpen] = useState(false);
    const [pendingConfig, setPendingConfig] = useState<{ key: string, value: string } | null>(null);

    const [equipaments, setEquipaments] = useState<Equipament[]>([]);
    const [checklists, setChecklists] = useState<ChecklistConfigVersion[]>([]);
    const [sectors, setSectors] = useState<string[]>([]);
    const [heartbeats, setHeartbeats] = useState<Record<string, string>>({});

    // Filtros
    const [selectedSector, setSelectedSector] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [idFilter, setIdFilter] = useState('');
    const [apelidoFilter, setApelidoFilter] = useState('');
    const [tidFilter, setTidFilter] = useState('');

    // Bulk selection
    const [bulkChecklistId, setBulkChecklistId] = useState('');
    const [bulkSkip, setBulkSkip] = useState('');
    const [bulkTimeout, setBulkTimeout] = useState('');
    const [bulkForceAutologin, setBulkForceAutologin] = useState('false');

    // Per-row changes: deviceId -> { checklistId?: string, skipMinutes?: string, timeoutMin?: string, forceAutologin?: string }
    const [changes, setChanges] = useState<Record<string, { checklistId?: string, skipMinutes?: string, timeoutMin?: string, forceAutologin?: string }>>({});

    const dbReady = !!operation;
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const loadData = useCallback(async () => {
        if (!operation) return;
        setLoading(true);
        setError(null);
        try {
            const [eqs, chks, secs] = await Promise.all([
                getEquipaments(operation, {}),
                getListChecklistConfigsVersions(operation),
                getSectors(operation),
            ]);
            setEquipaments(eqs);
            setChecklists(chks);
            setSectors(secs);

            AxiosInstance.get('/users-machine/get_version?db=' + operation, {
                withCredentials: true,
            }).then((resVersion: any) => {
                setUsersDBVersion(resVersion.data.version);
                if (resVersion.data.USER_MACHINE_ACCESS_MODE) {
                    setAccessMode(resVersion.data.USER_MACHINE_ACCESS_MODE);
                }
                if (resVersion.data.DOCUMENT_CONFIGURATION) {
                    setExpirationCheckMode(resVersion.data.DOCUMENT_CONFIGURATION);
                }
            }).catch(() => { console.log("get version error") });

            setChanges({}); // Reset changes on reload
        } catch (e: any) {
            setError(e?.message ?? 'Falha ao carregar dados');
        } finally {
            setLoading(false);
        }
    }, [operation]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (!operation || equipaments.length === 0) return;
        const validIds = equipaments.map(e => e.id).filter(Boolean);
        if (validIds.length === 0) return;

        AxiosInstance.post(`/heartbeats/get_last_heartbeats_per_id?db=${operation}`, {
            match_filters_dict: { id: validIds }
        }, {
            withCredentials: true
        }).then(res => {
            const hbMap: Record<string, string> = {};
            (res.data || []).forEach((hb: any) => {
                hbMap[hb.id] = hb.stamp;
            });
            setHeartbeats(hbMap);
        }).catch(err => console.error("Error fetching heartbeats", err));
    }, [equipaments, operation]);


    const filteredEquipaments = useMemo(() => {
        return equipaments.filter(eq => {
            if (selectedSector && eq.setor !== selectedSector) return false;
            if (typeFilter && !eq.tipo?.toLowerCase().includes(typeFilter.toLowerCase())) return false;
            if (idFilter && !eq.id?.toLowerCase().includes(idFilter.toLowerCase())) return false;
            if (apelidoFilter && !eq.apelido?.toLowerCase().includes(apelidoFilter.toLowerCase())) return false;
            if (tidFilter && !String(eq.tid || '').toLowerCase().includes(tidFilter.toLowerCase())) return false;
            return true;
        });
    }, [equipaments, selectedSector, typeFilter, idFilter, apelidoFilter, tidFilter]);

    const handleFieldChange = (deviceId: string, field: 'checklistId' | 'skipMinutes' | 'timeoutMin' | 'forceAutologin', value: string) => {
        setChanges(prev => ({
            ...prev,
            [deviceId]: {
                ...(prev[deviceId] || {}),
                [field]: value
            }
        }));
    };

    function handleConfigChange(key: string, value: string) {
        setPendingConfig({ key, value });
        setConfirmConfigOpen(true);
    }

    async function confirmConfigChange() {
        if (!pendingConfig || !operation) {
            setConfirmConfigOpen(false);
            return;
        }
        const { key, value } = pendingConfig;

        if (key === 'USER_MACHINE_ACCESS_MODE') setAccessMode(value);
        if (key === 'DOCUMENT_CONFIGURATION') setExpirationCheckMode(value);

        try {
            await saveCurrentOperationConfig(operation, {
                [key]: value
            } as any);
        } catch (e) {
            console.error("Failed to save config:", e);
        }
        setConfirmConfigOpen(false);
        setPendingConfig(null);
    }

    function cancelConfigChange() {
        setConfirmConfigOpen(false);
        setPendingConfig(null);
    }

    const handleBulkFieldApply = (field: 'checklistId' | 'skipMinutes' | 'timeoutMin' | 'forceAutologin', value: string) => {
        const newChanges = { ...changes };
        filteredEquipaments.forEach(eq => {
            newChanges[eq.id] = {
                ...(newChanges[eq.id] || {}),
                [field]: value
            };
        });
        setChanges(newChanges);
    };

    const handleSaveChanges = async () => {
        if (!operation) return;

        const deviceIdsToUpdate = Object.keys(changes);
        if (deviceIdsToUpdate.length === 0) {
            setSuccess("Nenhuma alteração para salvar.");
            return;
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const updates = deviceIdsToUpdate.map(deviceId => {
                const rowChanges = changes[deviceId];
                const eq = equipaments.find(e => e.id === deviceId);

                const checklistId = rowChanges.checklistId !== undefined ? rowChanges.checklistId : (eq?.cv || eq?.checklist_version || "");

                const skipMinutes = rowChanges.skipMinutes !== undefined
                    ? (rowChanges.skipMinutes === '' ? null : Number(rowChanges.skipMinutes))
                    : (eq?.checklist_skip_minutes ?? null);

                const timeoutMin = rowChanges.timeoutMin !== undefined
                    ? (rowChanges.timeoutMin === '' ? null : Number(rowChanges.timeoutMin))
                    : (eq?.checklist_timeout_min ?? null);

                const forceAutologin = rowChanges.forceAutologin !== undefined
                    ? (rowChanges.forceAutologin === 'true')
                    : (eq?.force_autologin ?? false);

                return {
                    device_id: deviceId,
                    checklist_config_id: checklistId,
                    checklist_skip_minutes: skipMinutes,
                    checklist_timeout_min: timeoutMin,
                    force_autologin: forceAutologin
                };
            });

            if (updates.length === 0) {
                setSuccess("Nenhuma atribuição válida para salvar (seleções vazias foram ignoradas).");
                setSaving(false);
                return;
            }

            await assignChecklistToDeviceBulk(operation, updates);

            setSuccess(`${deviceIdsToUpdate.length} equipamento(s) atualizado(s) com sucesso!`);
            loadData(); // Reload to confirm changes
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? 'Erro ao salvar alterações');
        } finally {
            setSaving(false);
        }
    };

    const handleExportCSV = () => {
        if (filteredEquipaments.length === 0) return;

        const headers = ["ID", "Apelido", "TID", "Tipo", "Setor", "Último Heartbeat", "Firmware", "Checklist", "Tempo sem Check.(Min)", "Tolerância Resposta", "Forçar Autologin"];

        const rows = filteredEquipaments.map(eq => {
            const hb = heartbeats[eq.id];
            let hbFormatted = "Sem Registro";
            if (hb) {
                const d = new Date(hb);
                if (!isNaN(d.getTime())) {
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = d.getFullYear();
                    const hours = String(d.getHours()).padStart(2, '0');
                    const minutes = String(d.getMinutes()).padStart(2, '0');
                    hbFormatted = `${day}/${month}/${year} ${hours}:${minutes}`;
                } else {
                    hbFormatted = String(hb);
                }
            }

            const currentChecklistId = changes[eq.id]?.checklistId !== undefined ? changes[eq.id].checklistId : (eq.cv || eq.checklist_version || "");
            const checklistName = checklists.find((c: any) => c._id === currentChecklistId)?.name || 'Sem checklist associado';

            const skipMinutes = changes[eq.id]?.skipMinutes !== undefined ? changes[eq.id].skipMinutes : (eq.checklist_skip_minutes !== null && eq.checklist_skip_minutes !== undefined ? String(eq.checklist_skip_minutes) : "");

            const timeoutMin = changes[eq.id]?.timeoutMin !== undefined ? changes[eq.id].timeoutMin : (eq.checklist_timeout_min !== null && eq.checklist_timeout_min !== undefined ? String(eq.checklist_timeout_min) : "");

            const forceAutologin = changes[eq.id]?.forceAutologin !== undefined ? changes[eq.id].forceAutologin : (eq.force_autologin !== null && eq.force_autologin !== undefined ? String(eq.force_autologin) : "false");

            return [
                `"${eq.id || ''}"`,
                `"${eq.apelido || '-'}"`,
                `"${eq.tid || '-'}"`,
                `"${eq.tipo || ''}"`,
                `"${eq.setor || ''}"`,
                `"${hbFormatted}"`,
                `"${String(eq.actual_firmware_version || eq.firmware_version || '-').replace(/"/g, '""')}"`,
                `"${checklistName}"`,
                `"${skipMinutes}"`,
                `"${timeoutMin}"`,
                `"${forceAutologin === 'true' ? 'Sim' : 'Não'}"`
            ].join(',');
        });

        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...rows].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `parametros_operacionais_${operation}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns: TableColumn<Equipament>[] = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '100px' },
        { name: 'Apelido', selector: row => row.apelido || '-', sortable: true, width: '100px' },
        { name: 'TID', selector: row => row.tid || '-', sortable: true, width: '100px' },
        { name: 'Tipo', selector: row => row.tipo, sortable: true, width: '120px' },
        { name: 'Setor', selector: row => row.setor, sortable: true, width: '150px' },
        {
            name: 'Último Heartbeat', selector: row => heartbeats[row.id] || 'Sem Registro', sortable: true, width: '160px', sortFunction: (rowA, rowB) => {
                const timeA = heartbeats[rowA.id] ? new Date(heartbeats[rowA.id]).getTime() : 0;
                const timeB = heartbeats[rowB.id] ? new Date(heartbeats[rowB.id]).getTime() : 0;
                return timeA - timeB;
            }, cell: row => {
                const hb = heartbeats[row.id];
                if (!hb) return <Typography variant="body2">Sem Registro</Typography>;

                // Format the string properly. Example incoming: "Fri, 04 Sep 2026 04:01:11 GMT"
                const d = new Date(hb);
                if (isNaN(d.getTime())) return <Typography variant="body2">{hb}</Typography>;

                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = String(d.getFullYear()).slice(-2);
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                const formatted = `${day}/${month}/${year} ${hours}:${minutes}`;

                return <Typography variant="body2">{formatted}</Typography>;
            }
        },
        { name: 'Firmware', selector: row => row.actual_firmware_version || row.firmware_version || '-', sortable: true, width: '130px' },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Checklist Atribuído</Typography>
                    <Select
                        size="small"
                        fullWidth
                        value={bulkChecklistId}
                        onChange={(e) => {
                            setBulkChecklistId(e.target.value as string);
                            handleBulkFieldApply('checklistId', e.target.value as string);
                        }}
                        displayEmpty
                        variant="outlined"
                        sx={{ fontSize: '0.875rem', height: 35 }}
                    >
                        <MenuItem value=""><em>Nenhum</em></MenuItem>
                        {checklists.map(c => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name} (v{c.version})
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>
            ),
            cell: (row) => {
                // Determine current value: changed value > existing value > empty
                const currentValue = changes[row.id]?.checklistId !== undefined ? changes[row.id].checklistId : (row.cv || row.checklist_version || "");

                return (
                    <Select
                        size="small"
                        fullWidth
                        value={currentValue}
                        onChange={(e) => handleFieldChange(row.id, 'checklistId', e.target.value as string)}
                        displayEmpty
                        variant="outlined"
                        sx={{ fontSize: '0.875rem', height: 35 }}
                    >
                        <MenuItem value="">
                            <em>Nenhum</em>
                        </MenuItem>
                        {checklists.map(c => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name} (v{c.version})
                            </MenuItem>
                        ))}
                    </Select>
                );
            },
            minWidth: '190px'
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Skip (min)</Typography>
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="420"
                        value={bulkSkip}
                        onChange={(e) => {
                            setBulkSkip(e.target.value);
                            handleBulkFieldApply('skipMinutes', e.target.value);
                        }}
                        variant="outlined"
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }}
                    />
                </Stack>
            ),
            cell: (row) => {
                const currentValue = changes[row.id]?.skipMinutes !== undefined
                    ? changes[row.id].skipMinutes
                    : (row.checklist_skip_minutes !== null && row.checklist_skip_minutes !== undefined ? String(row.checklist_skip_minutes) : "");

                return (
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="420"
                        value={currentValue}
                        onChange={(e) => handleFieldChange(row.id, 'skipMinutes', e.target.value)}
                        variant="outlined"
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }}
                    />
                );
            },
            width: '140px'
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Timeout (min)</Typography>
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="3"
                        value={bulkTimeout}
                        onChange={(e) => {
                            setBulkTimeout(e.target.value);
                            handleBulkFieldApply('timeoutMin', e.target.value);
                        }}
                        variant="outlined"
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }}
                    />
                </Stack>
            ),
            cell: (row) => {
                const currentValue = changes[row.id]?.timeoutMin !== undefined
                    ? changes[row.id].timeoutMin
                    : (row.checklist_timeout_min !== null && row.checklist_timeout_min !== undefined ? String(row.checklist_timeout_min) : "");

                return (
                    <TextField
                        size="small"
                        fullWidth
                        type="number"
                        placeholder="3"
                        value={currentValue}
                        onChange={(e) => handleFieldChange(row.id, 'timeoutMin', e.target.value)}
                        variant="outlined"
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }}
                    />
                );
            },
            width: '140px'
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Forçar Autologin</Typography>
                    <Select
                        size="small"
                        fullWidth
                        disabled={!isAdmin}
                        value={bulkForceAutologin}
                        onChange={(e) => {
                            setBulkForceAutologin(e.target.value as string);
                            handleBulkFieldApply('forceAutologin', e.target.value as string);
                        }}
                        displayEmpty
                        variant="outlined"
                        sx={{ fontSize: '0.875rem', height: 35 }}
                    >
                        <MenuItem value="true">Sim</MenuItem>
                        <MenuItem value="false">Não</MenuItem>
                    </Select>
                </Stack>
            ),
            cell: (row) => {
                let currentValue = changes[row.id]?.forceAutologin !== undefined
                    ? changes[row.id].forceAutologin
                    : (row.force_autologin !== null && row.force_autologin !== undefined ? String(row.force_autologin) : "false");

                if (!currentValue) currentValue = "false";

                return (
                    <Select
                        size="small"
                        fullWidth
                        disabled={!isAdmin}
                        value={currentValue}
                        onChange={(e) => handleFieldChange(row.id, 'forceAutologin', e.target.value as string)}
                        displayEmpty
                        variant="outlined"
                        sx={{ fontSize: '0.875rem', height: 35 }}
                    >
                        <MenuItem value="true">Sim</MenuItem>
                        <MenuItem value="false">Não</MenuItem>
                    </Select>
                );
            },
            width: '140px'
        },
    ];

    return (
        <DrawerHeader title="Parâmetros Operacionais">
            {(!dbReady || loading) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            bgcolor: 'background.paper',
                            borderLeft: `6px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Parâmetros Operacionais</Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                    Vincule os formulários de checklist e configure o comportamento individual (como acesso e tempos) das máquinas específicas da operação. Utilize filtros por setor ou tipo de equipamento para aplicar formulários em lote.
                                </Typography>
                            </Box>
                            <Box sx={{ bgcolor: theme.palette.grey[100], p: 2, borderRadius: 2, border: `1px solid ${theme.palette.grey[300]}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center', lineHeight: 1, mb: 0.5 }}>UV</Typography>
                                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', textAlign: 'center', lineHeight: 1 }}>{usersDBVersion}</Typography>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            bgcolor: 'background.paper',
                            borderLeft: `6px solid ${theme.palette.secondary.main || '#757575'}`,
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 600 }}>
                            Configurações global da Operação
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <FormControl sx={{ minWidth: 320 }} size="small">
                                <InputLabel>Permissão de condução por usuário</InputLabel>
                                <Select
                                    value={accessMode}
                                    onChange={(e) => handleConfigChange('USER_MACHINE_ACCESS_MODE', e.target.value as string)}
                                    label="Permissão de condução por usuário"
                                    disabled={!isAdmin}
                                >
                                    <MenuItem value="selected_machines">Acesso específico a máquina</MenuItem>
                                    <MenuItem value="selected_machine_types">Acesso a modelo de máquina</MenuItem>
                                    <MenuItem value="all_machines">Acesso a qualquer máquina</MenuItem>
                                    <MenuItem value="autologin">Login Automático</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 320 }} size="small">
                                <InputLabel>Configuração da gestão de documentos</InputLabel>
                                <Select
                                    value={expirationCheckMode}
                                    onChange={(e) => handleConfigChange('DOCUMENT_CONFIGURATION', e.target.value as string)}
                                    label="Configuração da gestão de documentos"
                                    disabled={!isAdmin}
                                >
                                    <MenuItem value="disabled">Desabilitado</MenuItem>
                                    <MenuItem value="informational">Apenas Informativo</MenuItem>
                                    <MenuItem value="block">Bloquear em vencimento</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Paper>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <Card>
                        <CardContent>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
                                <TextField
                                    select
                                    label="Filtrar Setor"
                                    value={selectedSector}
                                    onChange={(e) => setSelectedSector(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                    size="small"
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {sectors.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>

                                <TextField
                                    select
                                    label="Filtrar Tipo"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                    size="small"
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {Array.from(new Set(equipaments.map(e => e.tipo).filter(Boolean))).sort().map(t => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    label="Filtrar ID"
                                    value={idFilter}
                                    onChange={(e) => setIdFilter(e.target.value)}
                                    sx={{ minWidth: 120 }}
                                    size="small"
                                />

                                <TextField
                                    label="Filtrar Apelido"
                                    value={apelidoFilter}
                                    onChange={(e) => setApelidoFilter(e.target.value)}
                                    sx={{ minWidth: 120 }}
                                    size="small"
                                />

                                <TextField
                                    label="Filtrar TID"
                                    value={tidFilter}
                                    onChange={(e) => setTidFilter(e.target.value)}
                                    sx={{ minWidth: 120 }}
                                    size="small"
                                />

                                <Box flexGrow={1} />

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveChanges}
                                    disabled={saving || Object.keys(changes).length === 0}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                                </Button>
                            </Stack>


                        </CardContent>
                    </Card>

                    <Divider />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Equipamentos ({filteredEquipaments.length})</Typography>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleExportCSV}
                            disabled={filteredEquipaments.length === 0}
                        >
                            Exportar CSV
                        </Button>
                    </Box>

                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataTable
                            columns={columns}
                            data={filteredEquipaments}
                            pagination
                            paginationPerPage={50}
                            paginationRowsPerPageOptions={[25, 50, 100, 500, 1000]}
                            progressPending={loading}
                            persistTableHead
                            theme={isDark ? 'dark' : 'default'}
                            customStyles={{
                                headRow: {
                                    style: {
                                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.grey[100]
                                    }
                                }
                            }}
                        />
                    </Paper>
                </Stack>
            )}

            <Dialog
                open={confirmConfigOpen}
                onClose={cancelConfigChange}
                aria-labelledby="confirm-config-title"
                aria-describedby="confirm-config-description"
            >
                <DialogTitle id="confirm-config-title" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    Confirmar Alteração de Configuração
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-config-description" sx={{ mb: 2 }}>
                        Esta configuração afeta toda a operação e será aplicada <strong>imediatamente</strong>.
                    </DialogContentText>

                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" fontWeight={600} color="primary" gutterBottom>
                            Novo Comportamento Selecionado:
                        </Typography>

                        {pendingConfig?.key === 'USER_MACHINE_ACCESS_MODE' && (
                            <>
                                {pendingConfig.value === 'selected_machines' && <Typography variant="body2"><strong>Acesso específico a veículos:</strong> O usuário só poderá utilizar as máquinas que forem explicitamente selecionadas para ele. Isso oferece controle rigoroso sobre quem opera cada equipamento.</Typography>}
                                {pendingConfig.value === 'selected_machine_types' && <Typography variant="body2"><strong>Acesso por modelo de veículo:</strong> O usuário terá permissão para operar qualquer máquina que pertença ao modelo/tipo selecionado. Ideal para operadores qualificados em tipos específicos de máquinas.</Typography>}
                                {pendingConfig.value === 'all_machines' && <Typography variant="body2"><strong>Acesso liberado:</strong> O usuário poderá operar qualquer máquina da operação depois do login.</Typography>}
                                {pendingConfig.value === 'autologin' && <Typography variant="body2"><strong>Login Automatico</strong> As máquinas não exigirão login para serem operadas. Qualquer pessoa poderá ligar e usar a máquina. Isso desabilita o controle de acesso de forma geral.</Typography>}
                            </>
                        )}

                        {pendingConfig?.key === 'DOCUMENT_CONFIGURATION' && (
                            <>
                                {pendingConfig.value === 'disabled' && <Typography variant="body2"><strong>Desabilitado:</strong> O sistema não fará gestão de documentos e não monitorará validades.</Typography>}
                                {pendingConfig.value === 'informational' && <Typography variant="body2"><strong>Apenas Informativo:</strong> O sistema registrará os documentos vencidos no histórico (logs), mas o operador continuará podendo usar as máquinas normalmente.</Typography>}
                                {pendingConfig.value === 'block' && <Typography variant="body2"><strong>Bloquear em vencimento:</strong> Os operadores que estiverem com documentos vencidos serão bloqueados IMEDIATAMENTE e não poderão mais operar máquinas até que a situação seja regularizada.</Typography>}
                            </>
                        )}
                    </Box>
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Tem certeza que deseja continuar?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelConfigChange} color="inherit">Cancelar</Button>
                    <Button onClick={confirmConfigChange} color="primary" variant="contained" autoFocus>
                        Confirmar Alteração
                    </Button>
                </DialogActions>
            </Dialog>

        </DrawerHeader>
    );
}
